import React from "react";
import PropTypes from "prop-types";
import { Pencil, Trash2 } from "lucide-react";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import {
  UIButton,
  UIPageFormFooter,
  UIPageHeader,
  UIPhoneField,
  UISelect,
  UITextField,
  UIUpload,
} from "../../../../components/ui";
import DeleteEmployeeModal from "../../../Company-portal/TeamsPermissions/DeleteEmployeeModal";
import useAdminViewEmployee from "../hooks/useAdminViewEmployee";

import "../../../Company-portal/TeamsPermissions/AddEmployee/AddEmployee.scss";
import "../../../Company-portal/TeamsPermissions/ViewEmployee/ViewEmployee.scss";

const DetailField = ({ label, value, emptyValue, ltr }) => (
  <div className="view-employee__field">
    <span className="view-employee__field-label">{label}</span>
    <span className="view-employee__field-value" dir={ltr ? "ltr" : undefined}>
      {value || emptyValue}
    </span>
  </div>
);

DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  emptyValue: PropTypes.string,
  ltr: PropTypes.bool,
};

DetailField.defaultProps = {
  value: "",
  emptyValue: "—",
  ltr: false,
};

const AdminViewEmployee = () => {
  const {
    teamCopy,
    copy,
    formCopy,
    breadcrumbList,
    employee,
    loading,
    editing,
    deleting,
    busy,
    confirmingDelete,
    setConfirmingDelete,
    canEdit,
    canDelete,
    name,
    setName,
    phone,
    setPhone,
    phoneCountryIso,
    setPhoneCountryDialCode,
    role,
    setRole,
    roleOptions,
    pictureFile,
    picturePreview,
    setPictureFile,
    setPicturePreview,
    uploadState,
    uploadingPicture,
    showError,
    markTouched,
    errors,
    isDirty,
    isFormValid,
    enterEdit,
    cancelEdit,
    confirmDelete,
    handleUpdate,
    closeDeleteModal,
  } = useAdminViewEmployee();

  if (loading && !employee) {
    return null;
  }

  if (!employee) {
    return null;
  }

  return (
    <div className="team-management view-employee">
      <div className="mt-2">
        <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />
      </div>

      {!editing && (
        <UIPageHeader
          className="company-page-header view-employee__header"
          avatar={{
            image: employee.image || undefined,
            name: employee.name,
            size: "lg",
          }}
          title={employee.name}
          status={employee.role ? "role" : undefined}
          statusLabel={employee.role}
          statusTone="primarySolid"
          statusPreset="header"
          subtitle={
            <span className="view-employee__id" dir="ltr">
              {copy.idLabel} #{employee.id}
            </span>
          }
          actions={
            <>
              {canEdit && (
                <UIButton
                  type="button"
                  appearance="gradient"
                  size="large"
                  startIcon={<Pencil size={16} />}
                  onClick={enterEdit}
                  disabled={busy}
                >
                  {copy.editProfile}
                </UIButton>
              )}
              {canDelete && !employee.isCompanyOwner && (
                <UIButton
                  type="button"
                  color="error"
                  size="large"
                  startIcon={<Trash2 size={16} />}
                  onClick={() => setConfirmingDelete(true)}
                  disabled={deleting}
                >
                  {copy.deleteEmployee}
                </UIButton>
              )}
            </>
          }
        />
      )}

      <div className="view-employee__card">
        <div className="view-employee__card-header">{copy.detailsTitle}</div>

        {editing ? (
          <div className="add-employee__card-body">
            <div className="add-employee__avatar-col">
              <UIUpload
                name="profilePicture"
                className="add-employee__avatar"
                variant="logo"
                label={formCopy.pictureLabel}
                uploadText={formCopy.uploadPicture}
                changeText={formCopy.changePicture}
                preview={picturePreview || ""}
                file={pictureFile}
                showRemove
                isUploading={uploadingPicture}
                uploadProgress={uploadingPicture ? uploadState.percent : undefined}
                uploadProgressLabel={
                  uploadingPicture
                    ? `${formCopy.uploading} · ${uploadState.percent}%`
                    : null
                }
                onChange={(file, preview) => {
                  if (!file) {
                    setPictureFile(null);
                    setPicturePreview(null);
                    return;
                  }
                  setPictureFile(file);
                  setPicturePreview(typeof preview === "string" ? preview : null);
                }}
                disabled={busy}
              />
            </div>

            <div className="add-employee__grid">
              <UITextField
                label={copy.fields.name}
                name="name"
                required
                value={name}
                placeholder={formCopy.fields.namePlaceholder}
                onChange={(event) => setName(event.target.value)}
                onBlur={() => markTouched("name")}
                error={showError("name")}
                helperText={showError("name") ? errors.name : null}
              />
              <UITextField
                label={copy.fields.email}
                name="email"
                required
                value={employee.email}
                disabled
              />
              <UIPhoneField
                label={copy.fields.mobileNumber}
                name="mobileNumber"
                country={phoneCountryIso}
                preferredCountries={["eg", "sa", "ae"]}
                value={phone}
                formValueIsFullInternational
                disabled={busy}
                onChange={(fullValue, countryData) => {
                  setPhone(fullValue);
                  const dc = countryData?.dialCode;
                  if (dc != null && dc !== "") {
                    setPhoneCountryDialCode(String(dc).replace(/\D/g, ""));
                  }
                }}
              />
              <UISelect
                label={copy.fields.role}
                name="role"
                required
                placeholder={formCopy.fields.rolePlaceholder}
                options={roleOptions}
                value={role}
                disabled={busy}
                isOptionEqualToValue={(option, selected) =>
                  String(option?.id) === String(selected?.id)
                }
                onChange={(nextValue) => setRole(nextValue)}
                onBlur={() => markTouched("role")}
                error={showError("role")}
                helperText={showError("role") ? errors.role : null}
              />
            </div>
          </div>
        ) : (
          <div className="view-employee__grid">
            <DetailField
              label={copy.fields.name}
              value={employee.name}
              emptyValue={copy.emptyValue}
            />
            <DetailField
              label={copy.fields.email}
              value={employee.email}
              emptyValue={copy.emptyValue}
            />
            <DetailField
              label={copy.fields.mobileNumber}
              value={employee.mobileNumber}
              emptyValue={copy.emptyValue}
              ltr
            />
            <DetailField
              label={copy.fields.role}
              value={employee.role}
              emptyValue={copy.emptyValue}
            />
          </div>
        )}
      </div>

      {editing && (
        <UIPageFormFooter
          cancelLabel={copy.cancel}
          submitLabel={copy.update}
          onCancel={cancelEdit}
          onSubmit={handleUpdate}
          cancelDisabled={busy}
          submitDisabled={busy || !isDirty || !isFormValid}
          submitLoading={busy}
        />
      )}

      <DeleteEmployeeModal
        open={confirmingDelete}
        employee={employee}
        copy={teamCopy.employees.deleteModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
      />
    </div>
  );
};

export default AdminViewEmployee;
